import {
  Check,
  ChevronLeft,
  ChevronRight,
  FileText,
  LoaderCircle,
  RefreshCw,
  Save,
  Send
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { AgreementFormRenderer } from "@/components/AgreementFormRenderer";
import { AgreementPreview } from "@/components/AgreementPreview";
import { AgreementPreviewDownloadButton } from "@/components/AgreementPreviewDownloadButton";
import { ToastViewport, type ToastState } from "@/components/ToastViewport";
import { useSession } from "@/context/SessionContext";
import {
  addDaysToDateInputValue,
  buildAgreementFormDefaults,
  CORE_FORM_FIELD_KEYS,
  createDefaultAgreementFormSchema,
  getTodayDateInputValue,
  validateSection,
  validateSchema,
  type AgreementFormSchema,
  type AgreementFormValues
} from "@/lib/agreement-form-schema";
import {
  fetchCandidateWorkspace,
  saveAgreementDraft,
  submitAgreement,
  type CandidateAgreement,
  type AgreementSubmissionDetail,
  type AgreementSubmissionSummary,
  type CandidateWorkspace
} from "@/lib/api";

const ADDENDUM_HIDDEN_FIELD_KEYS = new Set<string>(["startDate", "endDate", "stipendAmount"]);
const CANDIDATE_READ_ONLY_FIELD_KEYS = new Set<string>();

function formatStipendSummary(value?: string) {
  if (!value) {
    return "Pending";
  }

  const digitsOnly = value.replace(/\D/g, "");

  if (!digitsOnly) {
    return value;
  }

  return `₹${new Intl.NumberFormat("en-IN").format(Number(digitsOnly))}/month`;
}

function formatLocalizedDateTime(value?: string) {
  if (!value) {
    return "Pending";
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short"
  });
}

export function CandidatePortalPage(props: { templateSlug?: string }) {
  const templateSlug = props.templateSlug;
  const { session, updateCandidate } = useSession();
  const [workspace, setWorkspace] = useState<CandidateWorkspace | null>(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!workspace?.latestSubmitted?.pdfBase64) {
      setPdfBlobUrl(null);
      return;
    }

    try {
      const bytes = Uint8Array.from(
        atob(workspace.latestSubmitted.pdfBase64),
        (char) => char.charCodeAt(0)
      );
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfBlobUrl(url);

      return () => {
        URL.revokeObjectURL(url);
      };
    } catch (e) {
      console.error("Failed to generate PDF Blob URL", e);
      setPdfBlobUrl(null);
    }
  }, [workspace?.latestSubmitted?.pdfBase64]);

  const [form, setForm] = useState<AgreementFormValues | null>(null);
  const [activeStep, setActiveStep] = useState<"form" | "agreement">("form");
  const [formErrors, setFormErrors] = useState<Record<string, Record<string, string>>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [lastSaveType, setLastSaveType] = useState<"auto" | "manual" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const lastSavedSnapshotRef = useRef("");
  const latestFormRef = useRef<AgreementFormValues | null>(null);
  const autoSaveInFlightRef = useRef(false);

  useEffect(() => {
    void loadWorkspace();
  }, [templateSlug]);

  async function loadWorkspace(options?: { silent?: boolean }) {
    if (!options?.silent) setIsLoading(true);
    setError(null);

    try {
      const response = await fetchCandidateWorkspace(templateSlug);
      const workingTemplateVersion =
        response.activeDraft?.templateVersion ??
        response.latestSubmitted?.templateVersion ??
        response.template.activeVersion;
      const nextForm = buildAgreementFormDefaults({
        schema: workingTemplateVersion.formSchema ?? createDefaultAgreementFormSchema(),
        source: response.activeDraft?.formData ?? response.latestSubmitted?.formData ?? {},
        templateDefaults: {
          companyAddress: workingTemplateVersion.companyAddress,
          confidentialityPeriod: workingTemplateVersion.defaults.confidentialityPeriod,
          jurisdictionCity: workingTemplateVersion.defaults.jurisdictionCity,
          workingHours: workingTemplateVersion.defaults.workingHours
        },
        user: {
          city: response.user.city,
          fullName: response.user.fullName
        }
      });

      setWorkspace(response);
      updateCandidate(response.user);
      setForm(nextForm);
      setLastSavedAt(response.activeDraft?.updatedAt ?? response.latestSubmitted?.updatedAt ?? null);
      latestFormRef.current = nextForm;
      lastSavedSnapshotRef.current = serializeDraftSnapshot(nextForm);
    } catch (requestError) {
      setError(
        requestError instanceof Error ? requestError.message : "Unable to load candidate workspace."
      );
    } finally {
      if (!options?.silent) setIsLoading(false);
    }
  }

  const liveAgreement = useMemo<CandidateAgreement>(() => form ?? {}, [form]);
  const workingTemplateVersion =
    workspace?.activeDraft?.templateVersion ??
    workspace?.latestSubmitted?.templateVersion ??
    workspace?.template.activeVersion;
  const previewTemplateVersion = workingTemplateVersion ?? workspace?.template.activeVersion ?? null;
  const formSchema = workingTemplateVersion?.formSchema;
  const hiddenFieldKeys = useMemo(() => {
    const hidden = new Set<string>();
    if (templateSlug === "addendum-offer-letter-india") {
      ADDENDUM_HIDDEN_FIELD_KEYS.forEach((key) => hidden.add(key));
    }

    if (form?.internshipType === "unpaid") {
      hidden.add("stipendAmount");
    }

    return hidden;
  }, [templateSlug, form?.internshipType]);

  const candidateFormSchema = useMemo(
    () => (formSchema ? omitFieldBlocks(formSchema, hiddenFieldKeys) : undefined),
    [formSchema, hiddenFieldKeys]
  );
  const formSectionIds = useMemo(() => {
    if (!candidateFormSchema) {
      return undefined;
    }

    return candidateFormSchema.sections
      .filter((section) => section.id !== "agreement-overview" && section.id !== "signature-consent")
      .map((section) => section.id);
  }, [candidateFormSchema]);
  const agreementStepSectionIds = useMemo(() => {
    if (!formSchema) {
      return undefined;
    }

    return formSchema.sections
      .filter((section) => section.id === "signature-consent")
      .map((section) => section.id);
  }, [formSchema]);
  const formStepValidationErrors = useMemo(() => {
    if (!candidateFormSchema || !form) {
      return {};
    }

    const nextErrors = Object.fromEntries(
      candidateFormSchema.sections
        .filter(
          (section) =>
            section.id !== "agreement-overview" &&
            section.id !== "signature-consent" &&
            !section.blocks.some((block) => block.type === "signature")
        )
        .map((section) => [section.id, validateSection(section, form)] as const)
        .filter((entry) => Object.keys(entry[1]).length > 0)
    ) as Record<string, Record<string, string>>;

    const startDate = getStringFieldValue(form, CORE_FORM_FIELD_KEYS.startDate).trim();
    const endDate = getStringFieldValue(form, CORE_FORM_FIELD_KEYS.endDate).trim();

    if (startDate && endDate && endDate < startDate) {
      nextErrors["internship-terms"] = {
        ...(nextErrors["internship-terms"] ?? {}),
        "end-date": "Internship end date must be on or after the start date."
      };
    }

    return nextErrors;
  }, [form, candidateFormSchema]);

  const flatErrors = useMemo(() => {
    const result: Record<string, string> = {};
    for (const sectionErrors of Object.values(formErrors)) {
      Object.assign(result, sectionErrors);
    }
    return result;
  }, [formErrors]);
  const lastSavedLabel = useMemo(() => {
    if (!lastSavedAt) return "Not saved yet";
    const prefix = lastSaveType === "manual" ? "Saved" : "Auto-saved";
    return `${prefix} ${formatLocalizedDateTime(lastSavedAt)}`;
  }, [lastSavedAt, lastSaveType]);

  useEffect(() => {
    latestFormRef.current = form;
  }, [form]);

  const readOnlyMode = Boolean(workspace?.latestSubmitted && !workspace?.activeDraft);
  const isUnlockedEditDraft = Boolean(workspace?.activeDraft?.sourceSubmissionId);
  const latestSubmittedVersion = workspace?.latestSubmitted ?? null;
  const canRestartAfterRejection = Boolean(
    workspace?.latestSubmitted?.approvalStatus === "rejected" && !workspace?.activeDraft
  );
  const [isRestarting, setIsRestarting] = useState(false);
  const previousSubmittedVersion = useMemo(() => {
    if (!workspace?.latestSubmitted?.sourceSubmissionId) {
      return null;
    }

    return (
      workspace.versions.find((version) => version.id === workspace.latestSubmitted?.sourceSubmissionId) ??
      null
    );
  }, [workspace]);
  const submissionChangeSummary = useMemo(() => {
    if (!latestSubmittedVersion?.sourceSubmissionId || !previousSubmittedVersion) {
      return [];
    }

    return getSubmissionChangeSummary(previousSubmittedVersion.summary, latestSubmittedVersion.summary);
  }, [latestSubmittedVersion?.sourceSubmissionId, latestSubmittedVersion?.summary, previousSubmittedVersion]);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const hasUnsavedChanges = Boolean(
    workspace &&
      form &&
      !readOnlyMode &&
      serializeDraftSnapshot(form) !== lastSavedSnapshotRef.current
  );

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (!workspace || !form || readOnlyMode || isLoading || isSubmitting) {
      return;
    }

    const snapshot = serializeDraftSnapshot(form);

    if (!lastSavedSnapshotRef.current || snapshot === lastSavedSnapshotRef.current) {
      return;
    }

    const timer = window.setTimeout(() => {
      if (autoSaveInFlightRef.current || snapshot === lastSavedSnapshotRef.current) {
        return;
      }

      autoSaveInFlightRef.current = true;
      setIsAutoSaving(true);

      void saveAgreementDraft(latestFormRef.current as Partial<CandidateAgreement>, templateSlug)
        .then((response) => {
          lastSavedSnapshotRef.current = snapshot;
          setLastSavedAt(response.submission.updatedAt);
          setLastSaveType("auto");
          setWorkspace((current) =>
            current
              ? {
                  ...current,
                  activeDraft: response.submission,
                  versions: upsertSubmissionSummary(current.versions, response.submission)
                }
              : current
          );
        })
        .catch(() => {
          setToast({
            tone: "error",
            title: "Auto-save failed",
            message: "Your draft could not be saved automatically. Use Save Draft to save manually."
          });
        })
        .finally(() => {
          autoSaveInFlightRef.current = false;
          setIsAutoSaving(false);
        });
    }, 1200);

    return () => window.clearTimeout(timer);
  }, [form, isLoading, isSubmitting, readOnlyMode, workspace]);

  if (isLoading || !workspace || !form || !previewTemplateVersion || session?.role !== "candidate") {
    return (
      <section className="mx-auto max-w-[1400px] px-6 py-12">
        <div className="surface flex min-h-[40vh] items-center justify-center gap-3 p-8 text-slate">
          <LoaderCircle className="h-5 w-5 animate-spin" />
          Loading candidate workspace
        </div>
      </section>
    );
  }

  function updateField(field: string, value: unknown) {
    setForm((current) => {
      if (!current) {
        return current;
      }

      const nextForm = {
        ...current,
        [field]: value
      };

      if (field === CORE_FORM_FIELD_KEYS.startDate) {
        const nextStartDate = typeof value === "string" ? value : "";
        const nextEndDate = getStringFieldValue(current, CORE_FORM_FIELD_KEYS.endDate);
        const minimumEndDate = nextStartDate
          ? addDaysToDateInputValue(nextStartDate, 1)
          : addDaysToDateInputValue(getTodayDateInputValue(), 1);

        if (nextEndDate && nextEndDate < minimumEndDate) {
          nextForm[CORE_FORM_FIELD_KEYS.endDate] = "";
        }
      }

      return nextForm;
    });
    setError(null);
    setFormErrors({});
  }

  function scrollToFirstValidationError(step: "form" | "agreement") {
    window.setTimeout(() => {
      const target =
        step === "form"
          ? document.querySelector('[data-testid="candidate-form-card"] .text-rose-700')
          : document.querySelector('[data-testid="candidate-signature-card"] .text-rose-700');

      target?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  }

  function handleNextStep() {
    if (Object.keys(formStepValidationErrors).length > 0) {
      setFormErrors(formStepValidationErrors);
      setError("Please fill in the missing required fields before you continue.");
      scrollToFirstValidationError("form");
      return;
    }

    setError(null);
    setFormErrors({});
    setActiveStep("agreement");
  }

  function handleStepChange(nextStep: "form" | "agreement") {
    if (nextStep === "agreement") {
      handleNextStep();
      return;
    }

    setError(null);
    setFormErrors({});
    setActiveStep("form");
  }

  async function handleSaveDraft() {
    setIsSavingDraft(true);
    setError(null);

    try {
      const response = await saveAgreementDraft(form as Partial<CandidateAgreement>, templateSlug);
      lastSavedSnapshotRef.current = serializeDraftSnapshot(response.submission.formData);
      setLastSavedAt(response.submission.updatedAt);
      setLastSaveType("manual");
      setWorkspace((current) =>
        current
          ? {
              ...current,
              activeDraft: response.submission,
              versions: upsertSubmissionSummary(current.versions, response.submission)
            }
          : current
      );
      setToast({
        tone: "success",
        title: "Draft saved",
        message: response.message
      });
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Unable to save the draft.");
    } finally {
      setIsSavingDraft(false);
    }
  }

  async function handleRestartAfterRejection() {
    if (!canRestartAfterRejection) {
      return;
    }

    setIsRestarting(true);
    setError(null);

    try {
      const response = await saveAgreementDraft({}, templateSlug);
      lastSavedSnapshotRef.current = serializeDraftSnapshot(response.submission.formData);
      setLastSavedAt(response.submission.updatedAt);
      setLastSaveType("manual");
      setWorkspace((current) =>
        current
          ? {
              ...current,
              activeDraft: response.submission,
              versions: upsertSubmissionSummary(current.versions, response.submission)
            }
          : current
      );
      setForm(response.submission.formData as CandidateAgreement);
      setActiveStep("form");
      setToast({
        tone: "success",
        title: "New draft started",
        message: "Update the details and submit again."
      });
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "Unable to start a new agreement draft."
      );
    } finally {
      setIsRestarting(false);
    }
  }

  async function handleSubmitAgreement() {
    const formValues = {
      ...(form ?? {}),
      [CORE_FORM_FIELD_KEYS.signedAt]: getTodayDateInputValue()
    };

    if (candidateFormSchema) {
      const validationResult = validateSchema(candidateFormSchema, formValues);
      const hasErrors = Object.values(validationResult).some(
        (sectionErrors) => Object.keys(sectionErrors).length > 0
      );

      if (hasErrors) {
        setFormErrors(validationResult);
        const hasFormStepErrors = Object.entries(validationResult).some(
          ([sectionId, sectionErrors]) =>
            sectionId !== "signature-consent" && Object.keys(sectionErrors).length > 0
        );
        setActiveStep(hasFormStepErrors ? "form" : "agreement");
        setError("Please fill in the missing required fields before you submit.");
        scrollToFirstValidationError(hasFormStepErrors ? "form" : "agreement");
        return;
      }
    }

    setFormErrors({});
    setIsSubmitting(true);
    setError(null);

    try {
      setForm(formValues);
      const response = await submitAgreement(formValues as any, templateSlug);
      setToast({
        tone: "success",
        title: "Agreement submitted",
        message: isUnlockedEditDraft
          ? "Your updated agreement was submitted. The admin can review it in the submissions list."
          : response.message
      });
      await loadWorkspace();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Unable to submit agreement.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section className="mx-auto max-w-[1500px] px-6 py-10 md:py-12" data-testid="candidate-page">
      <ToastViewport onDismiss={() => setToast(null)} toast={toast} />

      {workspace.user.mustChangePassword ? (
        <div
          className="rounded-[24px] border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900"
          data-testid="candidate-must-change-password-alert"
        >
          Your password must be changed before you continue using the portal fully. Visit{" "}
          <Link className="font-semibold text-amber-900 underline" to="/profile">
            the profile page
          </Link>{" "}
          to update it now.
        </div>
      ) : null}

      {error ? (
        <div
          className="rounded-[24px] border border-rose-200 bg-rose-50 px-5 py-4 text-sm font-medium text-rose-700"
          data-testid="candidate-error-message"
        >
          {error}
        </div>
      ) : null}

      <div className="space-y-6">
        {readOnlyMode ? (
          <>
            <div className="surface p-6" data-testid="candidate-record-card">
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div>
                  <p className="eyebrow">Your Details</p>
                  <h2 className="mt-2 text-3xl font-semibold tracking-tight text-ink">
                    {workspace.user.fullName}
                  </h2>
                  <div className="mt-3 space-y-1 text-sm leading-7 text-slate">
                    <p>
                      {workspace.user.phone
                        ? `${workspace.user.email} | ${workspace.user.phone}`
                        : workspace.user.email}
                    </p>
                    {workspace.user.university ? (
                      <p>
                        <span className="font-semibold text-ink">College:</span>{" "}
                        {workspace.user.university}
                      </p>
                    ) : null}
                  </div>
                </div>
                {workspace.latestSubmitted ? (
                  <div className="flex flex-col items-start gap-2 rounded-[22px] border border-line/70 bg-white/80 px-4 py-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate/70">
                      Latest Version
                    </p>
                    <p className="text-sm font-semibold text-ink">
                      v{workspace.latestSubmitted.versionNumber}
                      {workspace.latestSubmitted.submittedAt
                        ? ` · ${formatLocalizedDateTime(workspace.latestSubmitted.submittedAt)}`
                        : ""}
                    </p>
                    {previousSubmittedVersion ? (
                      <p className="text-xs font-medium text-slate">
                        Updated from v{previousSubmittedVersion.versionNumber}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="surface p-6" data-testid="candidate-submitted-agreement-card">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-[18px] bg-cloud text-royal">
                  <FileText className="h-5 w-5" />
                </div>
                <div>
                  <p className="eyebrow">Your Submission</p>
                  <h2 className="mt-2 text-3xl font-semibold tracking-tight text-ink">Submission status</h2>
                  <p className="mt-3 text-sm leading-7 text-slate">
                    Track review status and download your signed PDF once available.
                  </p>
                </div>
              </div>

              <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                <SummaryCard
                  label="Submitted"
                  value={formatLocalizedDateTime(workspace.latestSubmitted?.submittedAt)}
                />
                <ReadOnlyAgreementField
                  label="Admin review"
                  value={
                    workspace.latestSubmitted?.approvalStatus === "approved"
                      ? "Approved"
                      : workspace.latestSubmitted?.approvalStatus === "rejected"
                        ? `Rejected${
                            workspace.latestSubmitted.rejectionReason
                              ? ` · ${workspace.latestSubmitted.rejectionReason}`
                              : ""
                          }`
                        : "Pending approval"
                  }
                />
                {workspace.latestSubmitted?.reviewComment ? (
                  <ReadOnlyAgreementField
                    label="Admin feedback"
                    value={workspace.latestSubmitted.reviewComment}
                  />
                ) : null}
                <SummaryCard
                  label="Compensation"
                  value={formatStipendSummary(
                    getStringFieldValue(form, CORE_FORM_FIELD_KEYS.stipendAmount) ||
                    workspace.latestSubmitted?.summary.stipendAmount
                  )}
                />
                <ReadOnlyAgreementField
                  label="Confidentiality survival period"
                  value={
                    getStringFieldValue(form, CORE_FORM_FIELD_KEYS.confidentialityPeriod) ||
                    workspace.latestSubmitted?.summary.confidentialityPeriod ||
                    "Pending"
                  }
                />
              </div>

              {workspace.latestSubmitted?.approvalStatus === "approved" ? (
                <div className="mt-6 rounded-[24px] border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm text-emerald-900">
                  Your agreement was approved by the administrator.
                </div>
              ) : workspace.latestSubmitted?.approvalStatus === "rejected" ? (
                <div className="mt-6 space-y-4 rounded-[24px] border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-800">
                  <p>
                    Your agreement was rejected by the administrator. Review the feedback below and
                    resubmit after changes.
                  </p>
                  {canRestartAfterRejection ? (
                    <button
                      className="btn-secondary"
                      disabled={isRestarting}
                      onClick={() => void handleRestartAfterRejection()}
                      type="button"
                    >
                      {isRestarting ? (
                        <LoaderCircle className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                      Start new version
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className="mt-6 rounded-[24px] border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900">
                  Your agreement is pending administrator review.
                </div>
              )}

              {submissionChangeSummary.length ? (
                <div className="mt-6 rounded-[24px] border border-line/70 bg-white/88 p-5">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate/70">
                    Changes In This Update
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {submissionChangeSummary.slice(0, 8).map((item) => (
                      <span
                        className="rounded-full border border-line bg-white px-3 py-1 text-xs font-semibold text-ink"
                        key={item}
                      >
                        {item}
                      </span>
                    ))}
                    {submissionChangeSummary.length > 8 ? (
                      <span className="rounded-full border border-line bg-white px-3 py-1 text-xs font-semibold text-slate">
                        +{submissionChangeSummary.length - 8} more
                      </span>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {workspace.latestSubmitted ? (
                workspace.latestSubmitted.pdfBase64 ? (
                  <div className="mt-6 space-y-3">
                    <div className="flex flex-wrap gap-2">
                      <button
                        className="btn-primary"
                        onClick={() => {
                          const bytes = Uint8Array.from(
                            atob(workspace.latestSubmitted!.pdfBase64!),
                            (char) => char.charCodeAt(0)
                          );
                          const blob = new Blob([bytes], { type: "application/pdf" });
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement("a");
                          link.href = url;
                          link.download =
                            workspace.latestSubmitted!.pdfFileName ?? "gupio-agreement.pdf";
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          URL.revokeObjectURL(url);
                        }}
                        type="button"
                      >
                        Download PDF
                      </button>
                      <button
                        className="btn-secondary"
                        onClick={() => setShowPdfPreview((current) => !current)}
                        type="button"
                      >
                        {showPdfPreview ? "Hide preview" : "Preview PDF"}
                      </button>
                    </div>

                    {showPdfPreview ? (
                      <div className="overflow-hidden rounded-[24px] border border-line/70 bg-white">
                        <iframe
                          className="h-[560px] w-full"
                          src={pdfBlobUrl || ""}
                          title="Submitted agreement PDF"
                        />
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="mt-6 flex flex-col items-center gap-3 rounded-[24px] border border-dashed border-line/70 bg-cloud/40 px-6 py-10 text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-[18px] bg-cloud text-slate/60">
                      <FileText className="h-6 w-6" />
                    </div>
                    <p className="text-sm font-semibold text-ink">PDF preview not available yet</p>
                    <p className="max-w-xs text-xs leading-6 text-slate">
                      The signed agreement PDF will appear here once it has been generated. Check back shortly.
                    </p>
                  </div>
                )
              ) : null}
            </div>

          </>
        ) : (
          <>
            <div className="-mx-6 border-b border-line/90 bg-white/95 px-6">
              <CandidateFlowSteps
                activeStep={activeStep}
                canNavigateToAgreement={Object.keys(formStepValidationErrors).length === 0}
                onStepChange={handleStepChange}
              />
            </div>

            {activeStep === "form" ? (
              <>
                <div className="surface p-6" data-testid="candidate-record-card">
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div>
                      <p className="eyebrow">Your Details</p>
                      <h2 className="mt-2 text-3xl font-semibold tracking-tight text-ink">
                        {workspace.user.fullName}
                      </h2>
                      <p className="mt-3 text-sm leading-7 text-slate">
                        {workspace.user.phone
                          ? `${workspace.user.email} | ${workspace.user.phone}`
                          : workspace.user.email}
                      </p>
                    </div>
                    {workingTemplateVersion ? (
                      <div className="flex flex-col items-start gap-1.5 rounded-[22px] border border-line/70 bg-white/80 px-4 py-3">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate/70">
                          Agreement to Fill
                        </p>
                        <p className="text-sm font-semibold text-ink">
                          {workspace.template.title}
                        </p>
                        <p className="text-xs text-slate">
                          Version {workingTemplateVersion.versionNumber}
                        </p>
                      </div>
                    ) : null}
                  </div>
                </div>

                {candidateFormSchema ? (
                  <div data-testid="candidate-form-card">
                    <AgreementFormRenderer
                      errors={flatErrors}
                      emptyTokenValue="Pending"
                      onValueChange={updateField}
                      readOnlyFieldKeys={CANDIDATE_READ_ONLY_FIELD_KEYS}
                      schema={candidateFormSchema}
                      sectionIds={formSectionIds}
                      values={form}
                    />
                  </div>
                ) : null}

                <div className="surface flex items-center justify-end p-4 sm:p-5">
                  <button
                    className="btn-primary min-w-[132px] justify-center"
                    data-testid="candidate-next-step"
                    onClick={handleNextStep}
                    type="button"
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="eyebrow">Agreement Review</p>
                    <h2 className="mt-2 text-2xl font-semibold tracking-tight text-ink">
                      Review the final agreement
                    </h2>
                    <p className="mt-3 text-sm leading-7 text-slate">
                      Check the agreement details below, then sign and confirm before you submit.
                    </p>
                  </div>
                </div>

                <div data-testid="candidate-agreement-card">
                  <AgreementPreview
                    agreement={liveAgreement}
                    candidate={{
                      email: workspace.user.email,
                      fullName: workspace.user.fullName,
                      phone: workspace.user.phone,
                      submittedAt: workspace.latestSubmitted?.submittedAt
                    }}
                    showExecutionBlock={true}
                    templateVersion={previewTemplateVersion}
                  />
                </div>

                {formSchema && agreementStepSectionIds?.length ? (
                  <div data-testid="candidate-signature-card">
                    <AgreementFormRenderer
                      errors={flatErrors}
                      emptyTokenValue="Pending"
                      onValueChange={updateField}
                      schema={formSchema}
                      sectionIds={agreementStepSectionIds}
                      values={form}
                    />
                  </div>
                ) : null}

                <div className="surface space-y-4 p-4 sm:p-5">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${
                      isAutoSaving ? "text-slate/50" :
                      hasUnsavedChanges ? "text-amber-600" :
                      lastSaveType === "manual" ? "text-emerald-600" :
                      "text-slate/70"
                    }`}>
                      {isAutoSaving ? "Saving..." : hasUnsavedChanges ? "Unsaved changes" : lastSavedLabel}
                    </p>
                  </div>

                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <button
                      className="btn-secondary min-w-[132px] justify-center self-start"
                      data-testid="candidate-previous-step"
                      onClick={() => setActiveStep("form")}
                      type="button"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </button>

                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        className="btn-secondary"
                        data-testid="candidate-save-draft"
                        disabled={isSavingDraft}
                        onClick={() => void handleSaveDraft()}
                        type="button"
                      >
                        {isSavingDraft ? (
                          <LoaderCircle className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4" />
                        )}
                        Save Draft
                      </button>
                      <AgreementPreviewDownloadButton
                        agreement={liveAgreement}
                        candidate={{
                          email: workspace.user.email,
                          fullName: workspace.user.fullName
                        }}
                        templateVersion={previewTemplateVersion}
                      />
                      <button
                        className="btn-primary"
                        data-testid="candidate-submit-agreement"
                        disabled={isSubmitting}
                        onClick={() => void handleSubmitAgreement()}
                        type="button"
                      >
                        {isSubmitting ? (
                          <LoaderCircle className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                        Submit for Review
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function CandidateFlowSteps({
  activeStep,
  canNavigateToAgreement,
  onStepChange
}: {
  activeStep: "form" | "agreement";
  canNavigateToAgreement: boolean;
  onStepChange: (step: "form" | "agreement") => void;
}) {
  const steps = [
    {
      id: "form" as const,
      label: "Step 1",
      title: "Details"
    },
    {
      id: "agreement" as const,
      label: "Step 2",
      title: "Review & Sign"
    }
  ];
  const activeIndex = steps.findIndex((step) => step.id === activeStep);

  return (
    <nav aria-label="Agreement steps" className="py-4">
      <ol className="mx-auto flex w-full max-w-[520px] items-start justify-center">
        {steps.map((step, index) => {
          const status =
            index < activeIndex ? "complete" : index === activeIndex ? "current" : "upcoming";
          const isAgreementStep = step.id === "agreement";
          const isDisabled = isAgreementStep && !canNavigateToAgreement;

          return (
            <li
              className={`relative flex flex-col items-center ${index === steps.length - 1 ? "w-[118px] sm:w-[180px]" : "w-[118px] sm:w-[180px]"}`}
              key={step.id}
            >
              <button
                aria-disabled={isDisabled}
                className={`group flex w-full flex-col items-center text-center ${isDisabled ? "cursor-not-allowed opacity-60" : ""}`}
                data-testid={`candidate-step-${step.id}`}
                onClick={() => {
                  if (isDisabled) {
                    return;
                  }
                  onStepChange(step.id);
                }}
                type="button"
              >
                <span
                  className={`relative z-10 flex h-11 w-11 items-center justify-center rounded-full border-2 transition sm:h-12 sm:w-12 ${
                    status === "complete"
                      ? "border-emerald-500 bg-emerald-50 text-emerald-600"
                      : status === "current"
                        ? "border-navy bg-navy text-white shadow-glow"
                        : "border-line bg-white text-slate"
                  }`}
                >
                  {status === "complete" ? <Check className="h-5 w-5" /> : index + 1}
                </span>
                <span className="mt-4 block text-[10px] font-semibold uppercase tracking-[0.18em] text-slate/70 sm:text-[11px]">
                  {step.label}
                </span>
                <span
                  className={`mt-1 block text-center text-base font-semibold tracking-tight transition sm:text-lg ${
                    status === "current" ? "text-navy" : "text-ink"
                  }`}
                >
                  {step.title}
                </span>
              </button>

              {index < steps.length - 1 ? (
                <span
                  aria-hidden="true"
                  className={`absolute left-[calc(50%+1.75rem)] top-[1.35rem] h-[3px] w-[calc(100%-3.5rem)] rounded-full sm:left-[calc(50%+2rem)] sm:top-[1.45rem] sm:w-[calc(100%-4rem)] ${
                    index < activeIndex ? "bg-emerald-400" : "bg-line"
                  }`}
                />
              ) : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function getStringFieldValue(values: AgreementFormValues, key: string) {
  return typeof values[key] === "string" ? values[key] : "";
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[22px] border border-line/70 bg-white/88 p-4">
      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate/70">{label}</p>
      <p className="mt-2 text-sm font-semibold text-ink">{value}</p>
    </div>
  );
}

function ReadOnlyAgreementField({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-2">
        <span className="field-label">{label}</span>
        <span className="text-sm font-semibold text-rose-600">*</span>
      </div>
      <div className="input-field py-5 text-base font-medium text-ink">{value}</div>
    </div>
  );
}

function upsertSubmissionSummary(
  versions: AgreementSubmissionSummary[],
  submission: AgreementSubmissionDetail
) {
  const nextSummary: AgreementSubmissionSummary = {
    id: submission.id,
    versionNumber: submission.versionNumber,
    status: submission.status,
    templateVersionId: submission.templateVersionId,
    templateVersionNumber: submission.templateVersionNumber,
    submittedAt: submission.submittedAt,
    createdAt: submission.createdAt,
    updatedAt: submission.updatedAt,
    sourceSubmissionId: submission.sourceSubmissionId,
    summary: submission.summary
  };

  const existingIndex = versions.findIndex((version) => version.id === submission.id);

  if (existingIndex === -1) {
    return [nextSummary, ...versions].sort((left, right) => right.versionNumber - left.versionNumber);
  }

  return versions.map((version, index) => (index === existingIndex ? nextSummary : version));
}

function serializeDraftSnapshot(values: AgreementFormValues | CandidateAgreement | null) {
  if (!values) {
    return "";
  }

  const nextValues = { ...values };
  delete nextValues.internName;
  return JSON.stringify(nextValues);
}

function omitFieldBlocks(schema: AgreementFormSchema, fieldKeys: Set<string>): AgreementFormSchema {
  return {
    ...schema,
    sections: schema.sections
      .map((section) => ({
        ...section,
        blocks: section.blocks.filter(
          (block) => !("fieldKey" in block && fieldKeys.has(block.fieldKey))
        )
      }))
      .filter((section) => section.blocks.length > 0)
  };
}

function getSubmissionChangeSummary(previous: CandidateAgreement, current: CandidateAgreement) {
  const candidates: Array<{ key: keyof CandidateAgreement; label: string }> = [
    { key: "internAddress", label: "Intern address" },
    { key: "cityState", label: "City / state" },
    { key: "startDate", label: "Start date" },
    { key: "endDate", label: "End date" },
    { key: "internshipType", label: "Internship type" },
    { key: "stipendAmount", label: "Stipend" },
    { key: "workingHours", label: "Working hours" },
    { key: "confidentialityPeriod", label: "Confidentiality period" },
    { key: "jurisdictionCity", label: "Jurisdiction city" },
    { key: "signatureName", label: "Signature name" },
    { key: "signatureLocation", label: "Signature location" },
    { key: "signedAt", label: "Signed at" }
  ];

  const changed: string[] = [];

  for (const item of candidates) {
    const prevValue = previous[item.key];
    const nextValue = current[item.key];

    const prevNormalized = typeof prevValue === "string" ? prevValue.trim() : prevValue ?? "";
    const nextNormalized = typeof nextValue === "string" ? nextValue.trim() : nextValue ?? "";

    if (prevNormalized !== nextNormalized) {
      changed.push(item.label);
    }
  }

  return changed;
}
